import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useOfferPdfLayout, updateOfferPdfLayout, createOfferPdfLayout } from './useData';
import { useCompanyProfile, getCompanyLogoUrl } from '../settings/useData';
import { useNotification } from '../../contexts/NotificationContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Label } from '../../components/ui/Label';
import { Button } from '../../components/ui/Button';
import { GripVerticalIcon, DocumentTextIcon } from '../../components/ui/Icons';
import TextInput from '../../components/ui/TextInput';
import NumberInput from '../../components/ui/NumberInput';
import { Select } from '../../components/ui/Select';
import { Switch } from '../../components/ui/Switch';
import ColorPicker from '../../components/ui/ColorPicker';
import {
  DEFAULT_CLASSIC_DEFINITION,
  type OfferPdfTemplateDefinition,
  type OfferPdfSectionType,
  type OfferPdfSectionConfig,
  type OfferPdfSectionPosition,
} from '../../utils/offerPdfTypes';
import { exportOfferToPdf } from '../../utils/offerPdfExport';
import type { Offer } from '../offers/useData';

const ROUTES = {
  list: '/settings/offer-pdf-layouts',
  edit: (id: number) => `/settings/offer-pdf-layouts/${id}/edit`,
};

const SECTION_LABELS: Record<OfferPdfSectionType, string> = {
  accentBar: 'Accent bar',
  letterhead: 'Letterhead',
  titleMeta: 'Title & meta',
  intro: 'Intro text',
  lineItemsTable: 'Line items table',
  terms: 'Terms',
  notes: 'Notes',
  total: 'Total',
};

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => Math.round(x).toString(16).padStart(2, '0')).join('');
}

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return [0, 0, 0];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function buildMockOffer(): Offer {
  return {
    id: 1,
    offerNumber: 'PREVIEW-001',
    offerDate: new Date().toISOString().slice(0, 10),
    status: 'draft',
    createdById: 1,
    client: { clientId: 1, clientName: 'Sample Client', clientName2: null, clientEmail: null, clientAddress: null },
    introText: 'Sample intro for preview.',
    validityText: '30 days',
    deliveryText: 'Within 2 weeks',
    paymentMethodText: 'Bank transfer',
    notes: 'Preview only.',
    total: 1250.5,
    currencyCode: 'EUR',
    OfferLineItem: [
      {
        sortOrder: 0,
        description: 'Sample product',
        model: 'Model X',
        quantity: 2,
        unitPrice: 500,
        amount: 1000,
        catalogItemId: null,
        catalogItem: null,
      },
      {
        sortOrder: 1,
        description: 'Another item',
        model: null,
        quantity: 1,
        unitPrice: 250.5,
        amount: 250.5,
        catalogItemId: null,
        catalogItem: null,
      },
    ],
  };
}

export default function OfferPdfLayoutForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = id != null && id !== 'new';
  const numericId = isEdit ? parseInt(id, 10) : null;

  const { layout, isLoading: loadingLayout, mutate } = useOfferPdfLayout(numericId);
  const { mutate: globalMutate } = useSWRConfig();
  const { companyProfile } = useCompanyProfile();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();

  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [definition, setDefinition] = useState<OfferPdfTemplateDefinition>(() => ({ ...DEFAULT_CLASSIC_DEFINITION }));
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [draggedSectionIndex, setDraggedSectionIndex] = useState<number | null>(null);
  const { setTitleOverride } = useTopbar();

  useEffect(() => {
    if (layout) {
      setName(layout.name);
      setIsDefault(layout.isDefault);
      setDefinition(
        layout.definition && typeof layout.definition === 'object' && layout.definition.page && Array.isArray(layout.definition.sections) && layout.definition.tokens
          ? (layout.definition as OfferPdfTemplateDefinition)
          : { ...DEFAULT_CLASSIC_DEFINITION }
      );
    } else if (!isEdit) {
      setDefinition(JSON.parse(JSON.stringify(DEFAULT_CLASSIC_DEFINITION)));
      setName('');
      setIsDefault(false);
    }
  }, [layout, isEdit]);

  const updateDefinition = (patch: Partial<OfferPdfTemplateDefinition>) => {
    setDefinition((d) => ({ ...d, ...patch }));
  };

  const updatePage = (patch: Partial<OfferPdfTemplateDefinition['page']>) => {
    setDefinition((d) => ({ ...d, page: { ...d.page, ...patch } }));
  };

  const updateTokens = (patch: Partial<OfferPdfTemplateDefinition['tokens']>) => {
    setDefinition((d) => ({ ...d, tokens: { ...d.tokens, ...patch } }));
  };

  const updateSection = (index: number, patch: Partial<OfferPdfSectionConfig>) => {
    setDefinition((d) => {
      const sections = (d.sections || []).map((s, i) => (i === index ? { ...s, ...patch } : s));
      return { ...d, sections };
    });
  };

  const moveSection = (index: number, dir: 'up' | 'down') => {
    setDefinition((d) => {
      const sections = [...(d.sections || [])];
      const swap = dir === 'up' ? index - 1 : index + 1;
      if (swap < 0 || swap >= sections.length) return d;
      [sections[index], sections[swap]] = [sections[swap], sections[index]];
      sections.forEach((s, i) => {
        s.order = i;
      });
      return { ...d, sections };
    });
  };

  const reorderSections = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setDefinition((d) => {
      const sections = [...(d.sections || [])];
      const [removed] = sections.splice(fromIndex, 1);
      sections.splice(toIndex, 0, removed);
      sections.forEach((s, i) => {
        s.order = i;
      });
      return { ...d, sections };
    });
    setDraggedSectionIndex(null);
  };

  const updateTable = (patch: Partial<NonNullable<OfferPdfTemplateDefinition['table']>>) => {
    setDefinition((d) => ({ ...d, table: { ...(d.table || {}), ...patch } }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showError('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && numericId != null) {
        await updateOfferPdfLayout(numericId, { name: name.trim(), definition, isDefault });
        showSuccess('Layout updated');
      } else {
        await createOfferPdfLayout({ name: name.trim(), definition, isDefault });
        showSuccess('Layout created');
      }
      mutate();
      await globalMutate('/offer-pdf-layouts');
      navigate(ROUTES.list);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const logoUrl = companyProfile?.logoPath ? getCompanyLogoUrl() : undefined;
      const companyDetails = companyProfile
        ? {
            companyName: companyProfile.companyName,
            companyAddress: companyProfile.companyAddress,
            companyPhone: companyProfile.companyPhone,
            companyEmail: companyProfile.companyEmail,
            signatoryName: companyProfile.signatoryName,
          }
        : undefined;
      const blob = await exportOfferToPdf(buildMockOffer(), {
        templateDefinition: { ...definition },
        logoUrlOverride: logoUrl,
        logoRequiresAuth: !!companyProfile?.logoPath,
        companyDetails,
        returnBlob: true,
      });
      if (blob) {
        const url = URL.createObjectURL(blob);
        const opened = window.open(url, '_blank', 'noopener,noreferrer');
        if (!opened) {
          showError('Popup was blocked. Use the Download button below to open the preview.');
        } else {
          showSuccess('Preview opened in a new tab. If it didn\'t open, check popup blocking.');
        }
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownloadPreview = async () => {
    setDownloading(true);
    try {
      const logoUrl = companyProfile?.logoPath ? getCompanyLogoUrl() : undefined;
      const companyDetails = companyProfile
        ? {
            companyName: companyProfile.companyName,
            companyAddress: companyProfile.companyAddress,
            companyPhone: companyProfile.companyPhone,
            companyEmail: companyProfile.companyEmail,
            signatoryName: companyProfile.signatoryName,
          }
        : undefined;
      const blob = await exportOfferToPdf(buildMockOffer(), {
        templateDefinition: { ...definition },
        logoUrlOverride: logoUrl,
        logoRequiresAuth: !!companyProfile?.logoPath,
        companyDetails,
        returnBlob: true,
      });
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'offer-preview.pdf';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSuccess('Preview PDF downloaded.');
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    setTitleOverride(isEdit ? 'Edit layout' : 'New layout');
    return () => {
      setTitleOverride(undefined);
    };
  }, [isEdit, setTitleOverride]);

  if (isEdit && loadingLayout && !layout) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4" />
        <div className="animate-pulse h-64 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    );
  }

  const t = definition.tokens || {};
  const page = definition.page || DEFAULT_CLASSIC_DEFINITION.page;
  const sections = definition.sections ?? DEFAULT_CLASSIC_DEFINITION.sections;
  const table = definition.table ?? {};

  return (
    <div className="h-full flex flex-col">
        <div className="max-w-4xl mx-auto w-full min-w-0 flex flex-col h-full">
        <div
          className={`flex-1 flex flex-col min-w-0 ${colors.surface} rounded-lg shadow-sm border ${colors.border} overflow-hidden`}
        >
          <div className={`flex-1 min-w-0 overflow-y-auto overflow-x-hidden dashboard-scrollbar px-5 pt-4 pb-4`}>
          <div className="space-y-6 min-w-0 w-full">
        <div className="min-w-0 max-w-md">
          <Label htmlFor="name">
            Name <span className="text-red-500 dark:text-red-400" aria-hidden="true">*</span>
          </Label>
          <TextInput
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Layout name"
            className="mt-1 w-full"
            aria-required="true"
          />
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="isDefault"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <Label htmlFor="isDefault">Use as default</Label>
        </div>

        <div className={`border-t ${colors.border} pt-6`}>
          <h2 className={`text-sm font-medium ${colors.text} mb-3`}>Page</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Format</Label>
              <Select
                value={page.format}
                onChange={(e) => updatePage({ format: e.target.value as 'a4' | 'letter' })}
                options={[
                  { value: 'a4', label: 'A4' },
                  { value: 'letter', label: 'Letter' },
                ]}
                showClearButton={false}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Orientation</Label>
              <Select
                value={page.orientation}
                onChange={(e) => updatePage({ orientation: e.target.value as 'portrait' | 'landscape' })}
                options={[
                  { value: 'portrait', label: 'Portrait' },
                  { value: 'landscape', label: 'Landscape' },
                ]}
                showClearButton={false}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Margin (mm)</Label>
              <NumberInput
                value={page.marginMm}
                onChange={(e) => updatePage({ marginMm: Number(e.target.value) || 16 })}
                min={8}
                max={30}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <div className={`border-t ${colors.border} pt-6`}>
          <h2 className={`text-sm font-medium ${colors.text} mb-3`}>Table</h2>
          <div className="flex items-center gap-2 mb-3">
            <Switch
              id="showImages"
              checked={table.showProductImages !== false}
              onChange={(e) => updateTable({ showProductImages: e.target.checked })}
            />
            <Label htmlFor="showImages">Show product images</Label>
          </div>
          <div className="min-w-0 max-w-xs">
            <Label>Header style</Label>
            <Select
              value={table.headerStyle ?? 'filled'}
              onChange={(e) => updateTable({ headerStyle: e.target.value as 'filled' | 'bordered' | 'plain' })}
              options={[
                { value: 'filled', label: 'Filled' },
                { value: 'bordered', label: 'Bordered' },
                { value: 'plain', label: 'Plain' },
              ]}
              showClearButton={false}
              className="mt-1 w-full"
            />
          </div>
        </div>

        <div className={`border-t ${colors.border} pt-6`}>
          <h2 className={`text-sm font-medium ${colors.text} mb-1`}>Sections</h2>
          <p className={`text-xs ${colors.textMuted} mb-3`}>
            Enable sections and drag to reorder. Optionally set exact position (mm from top-left) per section; leave empty for automatic flow.
          </p>
          <ul className="space-y-3 min-w-0 w-full" role="list">
            {sections.map((section, index) => (
              <li
                key={section.id}
                role="listitem"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const from = Number(e.dataTransfer.getData('text/plain'));
                  if (Number.isFinite(from) && from !== index) reorderSections(from, index);
                }}
                className={`rounded-lg border p-3 transition-opacity min-w-0 overflow-hidden ${
                  draggedSectionIndex === index
                    ? 'border-blue-400 dark:border-blue-500 bg-blue-50/70 dark:bg-blue-900/30 opacity-80'
                    : `${colors.border} ${colors.surfaceHover}`
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    role="button"
                    tabIndex={0}
                    draggable={true}
                    aria-grabbed={draggedSectionIndex === index}
                    aria-label={`Drag to reorder section: ${SECTION_LABELS[section.type as OfferPdfSectionType] ?? section.type}`}
                    onDragStart={(e) => {
                      setDraggedSectionIndex(index);
                      e.dataTransfer.setData('text/plain', String(index));
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.dropEffect = 'move';
                      const li = e.currentTarget.closest('li');
                      if (li) e.dataTransfer.setDragImage(li, 0, 0);
                    }}
                    onDragEnd={() => setDraggedSectionIndex(null)}
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0 touch-none"
                    title="Drag to reorder"
                  >
                    <GripVerticalIcon className="w-5 h-5" />
                  </span>
                  <input
                    type="checkbox"
                    checked={section.enabled}
                    onChange={(e) => updateSection(index, { enabled: e.target.checked })}
                    className="rounded border-gray-300 shrink-0"
                  />
                  <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 min-w-0 truncate">
                    {SECTION_LABELS[section.type as OfferPdfSectionType] ?? section.type}
                  </span>
                </div>
                <div className={`ml-6 mt-3 pl-2 border-l-2 ${colors.border} min-w-0 max-w-full`}>
                  <div className={`${colors.textMuted} text-xs font-medium mb-1.5`}>Position (mm)</div>
                  <div className="grid grid-cols-4 gap-x-2 gap-y-1.5 min-w-0">
                    {(['xMm', 'yMm', 'widthMm', 'heightMm'] as const).map((key) => {
                      const label = key === 'xMm' ? 'X' : key === 'yMm' ? 'Y' : key === 'widthMm' ? 'W' : 'H';
                      const val = section.position?.[key];
                      const updatePos = (v: number | undefined) => {
                        const next = { ...section.position } as OfferPdfSectionPosition;
                        if (v === undefined || (typeof v === 'number' && isNaN(v))) {
                          delete next[key];
                        } else {
                          (next as Record<string, number>)[key] = v;
                        }
                        const hasAny = next.xMm != null || next.yMm != null || next.widthMm != null || next.heightMm != null;
                        updateSection(index, { position: hasAny ? next : undefined });
                      };
                      return (
                        <label key={key} className={`flex items-center gap-1 min-w-0 ${colors.textMuted}`}>
                          <span className="w-4 shrink-0 text-center text-xs">{label}</span>
                          <NumberInput
                            value={val ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              updatePos(v === null ? undefined : v);
                            }}
                            min={0}
                            max={key === 'xMm' || key === 'yMm' ? 400 : 300}
                            className="w-full min-w-0 max-w-[4rem] text-xs"
                            placeholder="—"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className={`border-t ${colors.border} pt-6`}>
          <h2 className={`text-sm font-medium ${colors.text} mb-3`}>Design tokens</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Accent color</Label>
              <ColorPicker
                value={t.accentColor ? rgbToHex(t.accentColor[0], t.accentColor[1], t.accentColor[2]) : '#2962ff'}
                onChange={(hex) => updateTokens({ accentColor: hexToRgb(hex) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Accent bar height (mm)</Label>
              <NumberInput
                value={t.accentBarHeightMm ?? 5}
                onChange={(e) => updateTokens({ accentBarHeightMm: Number(e.target.value) || 0 })}
                min={0}
                max={15}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Meta box color</Label>
              <ColorPicker
                value={
                  t.metaBoxColor
                    ? rgbToHex(t.metaBoxColor[0], t.metaBoxColor[1], t.metaBoxColor[2])
                    : '#f5f7fa'
                }
                onChange={(hex) => updateTokens({ metaBoxColor: hexToRgb(hex) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Terms box color</Label>
              <ColorPicker
                value={
                  t.termsBoxColor
                    ? rgbToHex(t.termsBoxColor[0], t.termsBoxColor[1], t.termsBoxColor[2])
                    : '#f8fafc'
                }
                onChange={(hex) => updateTokens({ termsBoxColor: hexToRgb(hex) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Table header color</Label>
              <ColorPicker
                value={
                  t.tableHeaderColor
                    ? rgbToHex(t.tableHeaderColor[0], t.tableHeaderColor[1], t.tableHeaderColor[2])
                    : '#34495e'
                }
                onChange={(hex) => updateTokens({ tableHeaderColor: hexToRgb(hex) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Border color</Label>
              <ColorPicker
                value={
                  t.borderColor
                    ? rgbToHex(t.borderColor[0], t.borderColor[1], t.borderColor[2])
                    : '#dcdfe4'
                }
                onChange={(hex) => updateTokens({ borderColor: hexToRgb(hex) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Title font size</Label>
              <NumberInput
                value={t.titleFontSize ?? 20}
                onChange={(e) => updateTokens({ titleFontSize: Number(e.target.value) || 20 })}
                min={12}
                max={28}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Body font size</Label>
              <NumberInput
                value={t.bodyFontSize ?? 9}
                onChange={(e) => updateTokens({ bodyFontSize: Number(e.target.value) || 9 })}
                min={7}
                max={14}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Small font size</Label>
              <NumberInput
                value={t.smallFontSize ?? 8}
                onChange={(e) => updateTokens({ smallFontSize: Number(e.target.value) || 8 })}
                min={6}
                max={12}
                className="mt-1"
              />
            </div>
          </div>
        </div>
          </div>
          </div>

          {/* Form actions – same style as GenericFormPage (crm-tasks) */}
          <div
            className={`flex-shrink-0 flex items-center justify-end space-x-3 p-6 border-t ${colors.border} ${colors.surfaceHover}`}
          >
            <Button type="button" variant="outline" onClick={() => navigate(ROUTES.list)}>
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              disabled={previewing}
              title="Open preview in a new tab"
            >
              {previewing ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2 align-middle" />
                  Generating…
                </>
              ) : (
                <>
                  <DocumentTextIcon className="w-4 h-4 mr-2 inline-block align-middle" />
                  Preview
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadPreview}
              disabled={downloading}
              title="Download preview PDF (use if popup was blocked)"
            >
              {downloading ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2 align-middle" />
                  Downloading…
                </>
              ) : (
                'Download PDF'
              )}
            </Button>
            <Button
              type="button"
              variant="accent"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
